/**
 * useOnboardingDriver — the onboarding-as-transcript cluster lifted out of
 * ChatBox (delegation pattern). Every hook body + dependency array is reproduced
 * VERBATIM from ChatBox: the stable greeting refs, the auto-greet effect,
 * beatMessages/onbIsDemo derivation, the demo-flag-clear effect, the beat guards,
 * the completeOnboarding/advanceBeat/connectBeat/runOnboardingPrompt/
 * onbExtractContacts/onbSaveProfileToDrive/persistOnboardingData callbacks, and
 * the driver useEffect that seeds welcome/scan/demo beats (incl. team branching).
 *
 * This is a CONTIGUOUS slice of what used to be inline (refs + useMemo +
 * useEffects + useCallbacks), so hook count/order is preserved exactly when
 * ChatBox calls it at the same position (after useChat + useOnboardingManager +
 * useVoiceMode). The cluster-local refs (beatAppendGuard, beatGuardReconciledRef,
 * onbDataSavedRef, advanceBeatBusyRef, requestGreetingRef, addAssistantMessageRef)
 * all live in here now — they were used only inside this cluster. The
 * module-level greetedConversationIds StrictMode set + live functions
 * (requestGreeting/addAssistantMessage/sendMessage) and orchestrator state are
 * threaded in by reference.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { isOnboardingComplete } from '@xavior/contracts';
import { HttpError } from '@/lib/http';
import { apiPost } from '@/lib/api/client';
import { api } from '@/lib/api';
import type { QueryClient } from '@tanstack/react-query';
import { pickGreeting, getLastMessageTimestamp, getRecentChatCount } from '@/lib/greetings';
import type { ChatMessage } from '@/features/chat/hooks/useChat';
import type { UseChatReturn } from '@/features/chat/hooks/useChat.types';
import type { Conversation } from '@/types/conversations';
import type { UserProfile, ContainerAccess } from '@/types/session';
import type { WorkspaceModule } from '@/lib/api/endpoints/workspaces';
import type { useOnboardingManager } from '../components/useOnboardingManager';
import { reconcileBeatGuard } from '../components/chatbox/onboarding-beat-guard';
import {
  ONBOARDING_BEAT_MARKER,
  encodeBeat,
  parseBeat,
  type BeatKind,
  type BeatPayload,
  type Bundle,
} from '../components/OnboardingBeats';

export interface UseOnboardingDriverDeps {
  activeConversation: Conversation | null;
  messages: ChatMessage[];
  isLoading: boolean;
  isStreaming: boolean;
  profile: UserProfile | null;
  token: string | null;
  workspaceId: string;
  selectedContainer: ContainerAccess | null;
  refreshSession: () => Promise<unknown> | unknown;
  queryClient: QueryClient;
  onboarding: ReturnType<typeof useOnboardingManager>;
  requestGreeting: UseChatReturn['requestGreeting'];
  addAssistantMessage: UseChatReturn['addAssistantMessage'];
  sendMessage: UseChatReturn['sendMessage'];
  greetedConversationIds: Set<string>;
}

export interface UseOnboardingDriverResult {
  onboardingActive: boolean;
  onboardingReplay: boolean;
  onbIsDemo: boolean;
  lastBeatId: string | null;
  advanceBeat: (kind: BeatKind, extra?: Partial<BeatPayload>) => Promise<void>;
  connectBeat: (kind: 'oauth.google.email' | 'oauth.microsoft.outlook' | 'demo' | 'use-existing') => Promise<void>;
  completeOnboarding: () => Promise<void>;
  runOnboardingPrompt: (p: string) => void;
  persistOnboardingData: (bundle: Bundle) => void;
  startOnboardingReplay: () => void;
}

export function useOnboardingDriver(deps: UseOnboardingDriverDeps): UseOnboardingDriverResult {
  const {
    activeConversation,
    messages,
    isLoading,
    isStreaming,
    profile,
    token,
    workspaceId,
    selectedContainer,
    refreshSession,
    queryClient,
    onboarding,
    requestGreeting,
    addAssistantMessage,
    sendMessage,
    greetedConversationIds,
  } = deps;

  // ── Onboarding active flag (derived early so startOnboardingReplay can use it) ───
  const onboardingActive = !!profile && !isOnboardingComplete(profile.onboardingState);

  // ── Replay flag ───────────────────────────────────────────────────────────
  // Set to true by /onboarding command; cleared when the beat ctx's onComplete
  // fires during replay (so normal completion logic is skipped).
  const [onboardingReplay, setOnboardingReplay] = useState(false);
  // Ref so the completeOnboarding guard can read the latest value without
  // needing onboardingReplay in its dependency array (closure staleness).
  const onboardingReplayRef = useRef(false);
  onboardingReplayRef.current = onboardingReplay;

  // /onboarding replays IN PLACE — the walkthrough beats append to the current
  // conversation rather than opening a fresh one (the earlier fresh-conversation
  // flow caused a jarring chat switch mid-command).
  const startOnboardingReplay = useCallback(() => {
    if (onboardingActive) return;
    if (!activeConversation?.id) return;
    setOnboardingReplay(true);
    addAssistantMessage(encodeBeat({ kind: 'welcome' }), activeConversation.id);
  }, [activeConversation?.id, addAssistantMessage, onboardingActive]);

  // Auto-greet: show an instant first message for every new conversation.
  // Onboarding (Welcome): fixed brand intro so every new user sees the same opening line.
  // Regular chats: random pickGreeting() + LLM follow-up via requestGreeting.
  // Stable refs so callbacks don't cause the effect to re-fire when they recreate.
  const requestGreetingRef = useRef(requestGreeting);
  const addAssistantMessageRef = useRef(addAssistantMessage);
  requestGreetingRef.current = requestGreeting;
  addAssistantMessageRef.current = addAssistantMessage;
  useEffect(() => {
    if (!activeConversation?.id) return;
    if (messages.length > 0) return;
    // Always wait for the message fetch to complete before adding the greeting —
    // otherwise the server response (even an empty array) wipes the greeting we just added.
    if (isLoading) return;
    // Don't fire if a greeting (or any stream) is already in progress — prevents
    // duplicate /chat requests when deps like profile or isLoading re-trigger this effect.
    if (isStreaming) return;
    // Wait for profile to load — profile is null during fetch, and
    // !null?.onboardingCompleted is true, which would incorrectly trigger onboarding.
    if (!profile) return;
    if (greetedConversationIds.has(activeConversation.id)) {
      // Recovery: if this conversation was previously marked as greeted but messages
      // are empty (greeting was wiped by a race condition), clear the flag and re-greet.
      // This provides a safety net if the primary fix (synchronous isLoading) is bypassed. (#1583)
      // Only allow one recovery attempt per conversation to prevent infinite loops.
      const recoveryKey = `recovered:${activeConversation.id}`;
      if (greetedConversationIds.has(recoveryKey)) return;
      greetedConversationIds.add(recoveryKey);
      greetedConversationIds.delete(activeConversation.id);
    }
    greetedConversationIds.add(activeConversation.id);

    const isOnboarding = !isOnboardingComplete(profile.onboardingState);
    if (isOnboarding) {
      // Pre-connect: the staged intro (OnboardingActions Welcome beat) IS the
      // greeting, so don't add a duplicate text greeting. Only greet once email
      // is connected, as a lead-in to the discovery scan.
      if (profile.onboardingState === 'email_connected') {
        addAssistantMessageRef.current(
          "You're in. Give me a few seconds — I'm reading the shape of your world, not the contents of it.",
          activeConversation.id,
          { routing: 'greeting' },
        );
      }
    } else {
      // Client-side greeting only — no LLM call.
      // Safe to use addAssistantMessage here because the `isLoading` guard above
      // ensures the message fetch has already completed — no subsequent setMessages([])
      // will wipe this greeting.
      // When we have real context to inject (unread emails, last conversation summary),
      // we can re-enable SSE greetings with meaningful content.
      const greeting = pickGreeting({
        lastMessageAt: getLastMessageTimestamp(),
        recentChatCount: getRecentChatCount(),
      });
      addAssistantMessageRef.current(greeting, activeConversation.id, { routing: 'greeting' });
    }
  }, [activeConversation?.id, activeConversation?.name, profile?.onboardingState, messages.length, isLoading, isStreaming]);

  // ── Onboarding-as-transcript driver ───────────────────────────────────────
  // Each beat (welcome → ask → scan → assessment → … → places) is a REAL
  // assistant chat message (content = ONBOARDING_BEAT_MARKER + JSON). They
  // accumulate into one persisted transcript that survives the OAuth round-trip,
  // completion, and a reload — the onboarding IS the chat. The user only clicks
  // to advance; their chat input is locked until the tour finishes.
  // (onboardingActive is derived earlier, before startOnboardingReplay, so the
  // replay guard can reference it without a temporal dead zone.)
  const beatMessages = useMemo(
    () => messages.filter((m) => m.role !== 'user' && typeof m.content === 'string' && m.content.startsWith(ONBOARDING_BEAT_MARKER)),
    [messages],
  );
  // Demo path → the persona is always Boris. Detect it from the localStorage flag
  // OR (bulletproof) the presence of a demo-intro beat, which only ever appears on
  // the demo path — so the persona never falls back to the real test account.
  const onbIsDemo = (() => {
    try { if (typeof window !== 'undefined' && localStorage.getItem('xavior:onb-demo') === '1') return true; }
    catch { /* noop */ }
    return beatMessages.some((m) => parseBeat(m.content as string)?.kind === 'demo-intro');
  })();

  // Clear the demo localStorage flag when onboarding is no longer active.
  // This handles the case where the API advance fails (e.g., due to upstream
  // contact/event import errors) but the profile state eventually reflects completion
  // or is manually corrected. Without this, the flag persists and locks every new chat
  // into demo mode even after onboarding should be done. See #3410.
  useEffect(() => {
    if (!onboardingActive && onbIsDemo) {
      try { if (typeof window !== 'undefined') localStorage.removeItem('xavior:onb-demo'); } catch { /* noop */ }
    }
  }, [onboardingActive]);

  const lastBeatId = beatMessages.length ? beatMessages[beatMessages.length - 1].id : null;
  const hasBeatKind = (kind: BeatKind) => beatMessages.some((m) => parseBeat(m.content as string)?.kind === kind);
  const beatAppendGuard = useRef<Set<string>>(new Set());
  // Tracks the beatMessages snapshot the guard self-heal last reconciled against
  // so it runs at most once per distinct render — the StrictMode double-invoke
  // shares one reference and must not delete a freshly-added key. See #3640/#3651.
  const beatGuardReconciledRef = useRef<readonly unknown[] | null>(null);

  const completeOnboarding = useCallback(async () => {
    // During replay mode: clear the flag and return — do NOT run state-machine
    // transitions or refreshSession so completed accounts stay complete.
    if (onboardingReplayRef.current) {
      setOnboardingReplay(false);
      return;
    }
    if (!token) return;
    try { if (typeof window !== 'undefined') localStorage.removeItem('xavior:onb-demo'); } catch { /* noop */ }
    const pathToComplete: Record<string, Array<'email_pitched' | 'email_connected' | 'email_skipped' | 'completed'>> = {
      pending: ['email_pitched', 'email_skipped', 'completed'],
      email_pitched: ['email_skipped', 'completed'],
      email_connected: ['completed'],
      email_skipped: ['completed'],
      completed: [],
    };
    try {
      // Try the direct edge first — works when the user already connected
      // (email_connected) or skipped (email_skipped), INCLUDING the member-connect
      // race where the closure's profile.onboardingState is stale right after the
      // OAuth refreshSession(). Only pending/email_pitched lack a direct edge to
      // 'completed' (team owners/members never run the solo email pitch).
      await api.onboarding.advance(token, { to: 'completed' });
    } catch (err) {
      if (err instanceof HttpError && err.status === 400) {
        // The illegal_transition 400 carries the server's ACTUAL current state —
        // walk a legal path from THAT, never the (possibly stale) closure state.
        const cur = (err.body as { currentState?: string } | undefined)?.currentState ?? 'pending';
        for (const to of pathToComplete[cur] ?? []) {
          try { await api.onboarding.advance(token, { to }); }
          catch (e) { if (!(e instanceof HttpError && e.status === 400)) { console.error('[onboarding] complete failed:', e); break; } }
        }
      } else {
        console.error('[onboarding] complete failed:', err);
      }
    }
    await refreshSession();
  }, [token, refreshSession]);

  // Append the next beat as a real assistant message. The 'walkthrough' beat is
  // the finale, but it no longer auto-completes onboarding: the finale itself is
  // interactive (real = "Start using Xavior"; demo = a live sassy re-offer to
  // connect, or "Maybe later"). Completion is driven by those buttons via
  // ctx.onComplete → completeOnboarding, so the demo re-offer stays live.
  const advanceBeatBusyRef = useRef(false);
  const advanceBeat = useCallback(async (kind: BeatKind, extra?: Partial<BeatPayload>) => {
    if (!activeConversation?.id) return;
    // Re-entrancy guard: one advance at a time (a fast double-click would
    // otherwise append the same beat twice).
    if (advanceBeatBusyRef.current) return;
    advanceBeatBusyRef.current = true;
    try {
      addAssistantMessage(encodeBeat({ kind, ...(extra ?? {}) }), activeConversation.id);
    } finally {
      advanceBeatBusyRef.current = false;
    }
  }, [activeConversation?.id, addAssistantMessage]);

  const connectBeat = useCallback(async (kind: 'oauth.google.email' | 'oauth.microsoft.outlook' | 'demo' | 'use-existing') => {
    if (!token) return;
    if (kind === 'demo') {
      try { if (typeof window !== 'undefined') localStorage.setItem('xavior:onb-demo', '1'); } catch { /* noop */ }
      await onboarding.handleDemoLoad();
      return;
    }
    // Already-connected: a real account with the needed scopes is present, so
    // skip OAuth entirely. Clear any demo flag and advance straight to
    // email_connected — the driver then seeds the real scan beat.
    if (kind === 'use-existing') {
      // /onboarding replay: reuse the already-synced discovery results — do
      // NOT restart discovery (that re-hits Gmail; replay exists for
      // rate-limit-free iteration). Seed the scan beat directly: the driver
      // effect that normally seeds it is gated off for completed accounts,
      // and the scan polls terminal onboarding_jobs and fast-forwards.
      if (onboardingReplayRef.current) {
        await advanceBeat('scan');
        return;
      }
      try { if (typeof window !== 'undefined') localStorage.removeItem('xavior:onb-demo'); } catch { /* noop */ }
      if (profile?.onboardingState === 'pending' || profile?.onboardingState === 'email_pitched') {
        try { await api.onboarding.advance(token, { to: 'email_connected' }); await refreshSession(); }
        catch (err) { if (!(err instanceof HttpError && err.status === 400)) console.error('[onboarding] use-existing advance failed:', err); }
      }
      // Already-connected real account: kick a FRESH discovery scan so the
      // personalized beats read the user's real world right now. This also
      // overwrites any stale discovery results from a prior demo run — those live
      // in onboarding_jobs and survive an onboarding reset, so without a fresh scan
      // the calendar/assessment beats can replay the demo persona's data (#3509).
      try {
        await api.onboarding.restartDiscovery(token);
      } catch (err) { console.warn('[onboarding] restart-discovery on use-existing failed (non-fatal):', err); }
      return;
    }
    // Connecting a REAL account. Capture whether the user had sampled demo data
    // (the "re-offer" case) BEFORE we touch anything, so a cancelled OAuth leaves
    // their sampled demo intact.
    const wasDemo = onbIsDemo;
    // Team workspaces (owner OR member) skip the solo owner state-machine
    // (email_pitched → email_connected → Boris/Marcus demo scan). Members complete
    // their per-workspace onboarding after the popup resolves; owners stay in the
    // setup checklist (connect → invite → seats) and finish it explicitly.
    const isTeamWs = selectedContainer?.type === 'team';
    const isTeamMember = isTeamWs && selectedContainer?.isOwner === false;
    if (!isTeamWs) {
      // OAuth: ensure we're in email_pitched so handleOAuthPopup echoes the click
      // as a user message, then open the consent + popup flow. On success it
      // refreshes the session to email_connected, which triggers the scan beat.
      if (profile?.onboardingState === 'pending') {
        try { await api.onboarding.advance(token, { to: 'email_pitched' }); await refreshSession(); }
        catch (err) { if (!(err instanceof HttpError && err.status === 400)) console.error('[onboarding] pitch advance failed:', err); }
      }
    }
    const connected = await onboarding.handleOAuthPopup(kind);
    if (!connected) return; // cancelled — leave demo sampling (if any) untouched.
    // Real account connected — exit demo mode so the Marcus persona and demo-only
    // fallbacks never leak onto real data (#3509).
    try { if (typeof window !== 'undefined') localStorage.removeItem('xavior:onb-demo'); } catch { /* noop */ }
    // Team members: their onboarding completes on successful OAuth return, not
    // before. The team-welcome beat opens the popup first (user gesture) and
    // delegates completion here so refreshSession() never races the open popup.
    if (isTeamMember) {
      await completeOnboarding();
      return;
    }
    // Team OWNER: stay in the setup checklist beat (connect → invite → seats);
    // it re-checks connection on focus. Don't fall through to the solo
    // scan/reveal — owners never run the Boris/Marcus demo flow.
    if (isTeamWs) return;
    if (wasDemo) {
      // Re-offer accepted: wipe the seeded demo data (same switch-to-real endpoint
      // the Email module / demo.switch-to-real chip use) so the real workspace is
      // clean, then drop the user into their now-real, syncing workspace rather
      // than replaying the walkthrough.
      try {
        await api.demoSeed.switchDemoToReal(token);
        queryClient.invalidateQueries({ predicate: (q) => { const k = JSON.stringify(q.queryKey); return k.includes('email') || k.includes('gmail') || k.includes('integration') || k.includes('contact') || k.includes('object'); } });
      } catch (err) { console.warn('[onboarding] demo wipe after real connect failed (non-fatal):', err); }
      await completeOnboarding();
    }
    // Initial connect (wasDemo=false): fall through — the driver seeds the real
    // scan and runs the personalized reveal.
  }, [token, profile?.onboardingState, refreshSession, onboarding, onbIsDemo, queryClient, completeOnboarding, advanceBeat, selectedContainer?.type, selectedContainer?.isOwner]);

  const runOnboardingPrompt = useCallback((p: string) => {
    if (activeConversation?.id) void sendMessage(p, activeConversation.id);
  }, [activeConversation?.id, sendMessage]);

  const onbExtractContacts = useCallback(async () => {
    const r = await api.onboarding.extractContacts(token);
    queryClient.invalidateQueries({ predicate: (q) => JSON.stringify(q.queryKey).includes('contact') });
    return { created: r.created, enriched: r.enriched };
  }, [token, queryClient]);

  const onbSaveProfileToDrive = useCallback(async (markdown: string) => {
    if (!token || !workspaceId) return false;
    const res = await api.workspaces.listWorkspaceModules(token, workspaceId, {});
    const mods: WorkspaceModule[] = Array.isArray(res) ? res : ((res as { items?: WorkspaceModule[] }).items ?? []);
    // Drive-family only — the onboarding profile is a rich doc and must NOT
    // land in the standalone plain-text Notes module (type 'notes').
    const m = mods.find((x) => !x.archivedAt && ['drive', 'docs', 'note_list'].includes(x.type));
    if (!m) return false;
    await apiPost('/notes', { title: 'Your XAVIOR profile', body: markdown, moduleLinks: [{ moduleId: m.id }] });
    queryClient.invalidateQueries({ predicate: (q) => JSON.stringify(q.queryKey).includes('note') });
    return true;
  }, [token, workspaceId, queryClient]);

  // Under the hood: the instant the scan data lands, silently persist the user's
  // extracted contacts + a profile doc to Drive — once per workspace, best-effort,
  // no UI. (Replaces the old visible "setup" beat.)
  const onbDataSavedRef = useRef(false);
  const persistOnboardingData = useCallback((bundle: Bundle) => {
    if (onbDataSavedRef.current) return;
    const guardKey = `xavior:onb-saved:${workspaceId ?? 'x'}`;
    try {
      if (typeof window !== 'undefined' && localStorage.getItem(guardKey) === '1') { onbDataSavedRef.current = true; return; }
    } catch { /* noop */ }
    onbDataSavedRef.current = true;
    try { if (typeof window !== 'undefined') localStorage.setItem(guardKey, '1'); } catch { /* noop */ }
    void onbExtractContacts().catch(() => {});
    const heading = `# ${bundle.name}${bundle.company ? ` — ${bundle.company}` : ''}`;
    const body = bundle.assessSections.length
      ? bundle.assessSections.map((s) => `## ${s.title}\n${s.body}`).join('\n\n')
      : bundle.assess.map((r) => `- ${r.text}`).join('\n');
    if (body) void onbSaveProfileToDrive(`${heading}\n\n${body}`).catch(() => {});
  }, [workspaceId, onbExtractContacts, onbSaveProfileToDrive]);

  // Driver: seed the welcome beat at onboarding start, and the scan beat once
  // the account connects. Idempotent across reload (guards on persisted beats).
  useEffect(() => {
    if (!profile || !activeConversation?.id || isLoading) return;
    // Deliberately USER-LEVEL gated, including for the team-owner-welcome beat
    // (#3949 reviewed + kept): payment paths set only the legacy
    // users.onboarding_completed boolean, NOT onboarding_state, so a brand-new
    // Team buyer still passes this check and gets the owner beat. The one
    // persona this misses — an owner who completed onboarding elsewhere and
    // lands in a fresh team workspace — cannot be keyed per-workspace today:
    // owners have no membership row, and the containers field ORs the
    // user-level boolean in. Weakening this early-return for team owners would
    // re-seed the owner checklist into EVERY new conversation of long-standing
    // team owners (the append guard below is per-conversation, not
    // per-workspace), which is the worse regression.
    if (isOnboardingComplete(profile.onboardingState)) return;
    const convId = activeConversation.id;
    // Self-heal the append guard so a server GET that wipes the optimistic
    // scan/demo-intro beat (see #3640) can re-fire the append. Reconciles at
    // most once per distinct beatMessages reference so the StrictMode dev
    // double-invoke (same snapshot, setMessages not yet flushed) can't delete a
    // freshly-added key and double-append (#3651). hasBeatKind() below still
    // prevents duplicates once the beat is actually present/persisted.
    reconcileBeatGuard({ convId, beatMessages, beatAppendGuard, lastReconciledRef: beatGuardReconciledRef, hasBeatKind });
    if (profile.onboardingState === 'email_connected') {
      // Demo path: frame the data as "Boris @ Anthropic" before the scan. The
      // demo-intro beat's button advances to 'scan'. Real connect → scan directly.
      if (onbIsDemo && !hasBeatKind('scan')) {
        const dk = `demo-intro:${convId}`;
        if (beatAppendGuard.current.has(dk) || hasBeatKind('demo-intro')) { beatAppendGuard.current.add(dk); return; }
        beatAppendGuard.current.add(dk);
        addAssistantMessageRef.current(encodeBeat({ kind: 'demo-intro' }), convId);
        return;
      }
      const k = `scan:${convId}`;
      if (beatAppendGuard.current.has(k) || hasBeatKind('scan') || hasBeatKind('demo-intro')) { beatAppendGuard.current.add(k); return; }
      beatAppendGuard.current.add(k);
      addAssistantMessageRef.current(encodeBeat({ kind: 'scan' }), convId);
    } else {
      // Team workspaces get their own onboarding instead of the solo Boris demo:
      // an invited member (role !== owner) sees the lightweight welcome beat; the
      // team OWNER walks the setup checklist (connect → invite → seats).
      const isTeamWs = selectedContainer?.type === 'team';
      const isTeamMember = isTeamWs && selectedContainer?.isOwner === false;
      const beatKind: BeatKind = isTeamMember
        ? 'team-welcome'
        : isTeamWs
          ? 'team-owner-welcome'
          : 'welcome';
      const k = `${beatKind}:${convId}`;
      if (beatAppendGuard.current.has(k) || beatMessages.length > 0) { beatAppendGuard.current.add(k); return; }
      beatAppendGuard.current.add(k);
      addAssistantMessageRef.current(encodeBeat({ kind: beatKind }), convId);
    }
  }, [profile?.onboardingState, activeConversation?.id, isLoading, beatMessages.length, selectedContainer?.type, selectedContainer?.isOwner]);

  return {
    onboardingActive: onboardingActive || onboardingReplay,
    onboardingReplay,
    onbIsDemo,
    lastBeatId,
    advanceBeat,
    connectBeat,
    completeOnboarding,
    runOnboardingPrompt,
    persistOnboardingData,
    startOnboardingReplay,
  };
}
